/**
 * InteractiveExercise - Renders interactive lesson content with instructions and hints.
 */

import { memo, useState, useCallback } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { CheckCircle2, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { InteractiveContent } from '../../curriculum/manager'

export interface InteractiveExerciseProps {
  /** The interactive content to display */
  content: InteractiveContent
  /** Callback when exercise is marked complete */
  onComplete: () => void
  /** Whether already completed */
  isCompleted?: boolean
  /** Optional className */
  className?: string
}

/**
 * InteractiveExercise displays instructions and hints for hands-on exercises.
 */
export const InteractiveExercise = memo(function InteractiveExercise({
  content,
  onComplete,
  isCompleted = false,
  className,
}: InteractiveExerciseProps) {
  const { instructions, hints } = content
  const [showHints, setShowHints] = useState(false)
  const [currentHintIndex, setCurrentHintIndex] = useState(0)

  const handleShowNextHint = useCallback(() => {
    if (currentHintIndex < hints.length - 1) {
      setCurrentHintIndex((prev) => prev + 1)
    }
  }, [currentHintIndex, hints.length])

  const toggleHints = useCallback(() => {
    setShowHints((prev) => !prev)
  }, [])

  return (
    <div className={cn('space-y-4', className)}>
      {/* Instructions */}
      <div
        className={cn(
          'prose prose-sm max-w-none dark:prose-invert',
          'prose-headings:font-semibold prose-headings:text-foreground',
          'prose-h2:text-xl prose-h2:mb-3',
          'prose-p:text-foreground prose-p:leading-relaxed',
          'prose-ul:text-foreground prose-ol:text-foreground',
          'prose-li:marker:text-muted-foreground',
          'prose-strong:text-primary',
          'prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground'
        )}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{instructions}</ReactMarkdown>
      </div>

      {/* Hints section */}
      {hints.length > 0 && (
        <div className="rounded-lg border border-border bg-card">
          <button
            type="button"
            onClick={toggleHints}
            className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/50"
          >
            <div className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              <span className="text-sm font-medium text-foreground">
                Need a hint?
              </span>
            </div>
            {showHints ? (
              <ChevronUp className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            )}
          </button>

          {showHints && (
            <div className="border-t border-border p-3 space-y-2">
              {hints.slice(0, currentHintIndex + 1).map((hint, idx) => (
                <div
                  key={idx}
                  className="flex items-start gap-2 text-sm text-muted-foreground"
                >
                  <span className="font-medium text-amber-600 dark:text-amber-400">
                    {idx + 1}.
                  </span>
                  <span>{hint}</span>
                </div>
              ))}

              {currentHintIndex < hints.length - 1 && (
                <button
                  type="button"
                  onClick={handleShowNextHint}
                  className="mt-2 text-sm text-primary hover:underline"
                >
                  Show another hint ({currentHintIndex + 1}/{hints.length})
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Completion button */}
      <div className="flex justify-center pt-2">
        {isCompleted ? (
          <div className="flex items-center gap-2 rounded-md bg-green-500/10 px-4 py-2 text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-5 w-5" />
            <span className="text-sm font-medium">Exercise completed</span>
          </div>
        ) : (
          <button
            type="button"
            onClick={onComplete}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <CheckCircle2 className="h-4 w-4" />
            Mark as Complete
          </button>
        )}
      </div>
    </div>
  )
})
